import { db } from './firestoreDb';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
} from 'firebase/firestore';
import { NewsArticle, HomepageLayoutConfig } from '../types/news';
import { translatePostOrArticleAsync } from './translationService';

const STORAGE_KEY_ARTICLES = 'clm_news_articles_v1';
const STORAGE_KEY_LAYOUT = 'clm_homepage_layout_v1';
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes TTL for blazing-fast home page loads

export const DEFAULT_HOMEPAGE_LAYOUT: HomepageLayoutConfig = {
  newsStripEnabled: true,
  pinnedArticleId: null,
  maxArticlesToShow: 4,
  sectionTitleEn: 'Latest Real Estate News & Market Insights',
  sectionTitleFr: 'Dernières Actualités & Analyses du Marché Foncier',
};

// No mock or automatic articles: Every single article MUST be explicitly approved by a human before publishing.
export const DEFAULT_NEWS_ARTICLES: NewsArticle[] = [];

// In-memory cache variables for instant rendering with 0 network overhead
let memoryArticlesCache: { data: NewsArticle[]; timestamp: number } | null = null;
let memoryLayoutCache: { data: HomepageLayoutConfig; timestamp: number } | null = null;

function getCachedItem<T>(key: string): T | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed.data as T;
  } catch {
    return null;
  }
}

function setCachedItem<T>(key: string, data: T): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {}
}

export const newsService = {
  /**
   * Fetch Homepage Layout Configuration with aggressive caching for 0ms overhead
   */
  async getHomepageLayoutConfig(): Promise<HomepageLayoutConfig> {
    if (memoryLayoutCache && Date.now() - memoryLayoutCache.timestamp < CACHE_TTL_MS) {
      return memoryLayoutCache.data;
    }

    const local = getCachedItem<HomepageLayoutConfig>(STORAGE_KEY_LAYOUT);
    if (local) {
      memoryLayoutCache = { data: local, timestamp: Date.now() };
      return local;
    }

    if (!db) {
      return DEFAULT_HOMEPAGE_LAYOUT;
    }

    try {
      const docRef = doc(db, 'system_settings', 'homepage_layout');
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const config = { ...DEFAULT_HOMEPAGE_LAYOUT, ...snap.data() } as HomepageLayoutConfig;
        memoryLayoutCache = { data: config, timestamp: Date.now() };
        setCachedItem(STORAGE_KEY_LAYOUT, config);
        return config;
      }
    } catch (err) {
      console.warn('[NEWS SERVICE] Failed to load homepage layout config:', err);
    }

    return DEFAULT_HOMEPAGE_LAYOUT;
  },

  /**
   * Save Homepage Layout Configuration (Admin action)
   */
  async saveHomepageLayoutConfig(config: HomepageLayoutConfig, adminEmail: string): Promise<boolean> {
    const updated: HomepageLayoutConfig = {
      ...config,
      updatedAt: new Date().toISOString(),
      updatedBy: adminEmail,
    };

    memoryLayoutCache = { data: updated, timestamp: Date.now() };
    setCachedItem(STORAGE_KEY_LAYOUT, updated);

    if (db) {
      try {
        const docRef = doc(db, 'system_settings', 'homepage_layout');
        await setDoc(docRef, updated, { merge: true });
        return true;
      } catch (err) {
        console.error('[NEWS SERVICE] Error saving homepage layout config:', err);
        return false;
      }
    }

    return true;
  },

  /**
   * Fetch published news articles (Fast, cached, 0ms home page load)
   */
  async getPublishedArticles(options?: { forceRefresh?: boolean }): Promise<NewsArticle[]> {
    if (!options?.forceRefresh) {
      if (memoryArticlesCache && Date.now() - memoryArticlesCache.timestamp < CACHE_TTL_MS) {
        return memoryArticlesCache.data;
      }

      const local = getCachedItem<NewsArticle[]>(STORAGE_KEY_ARTICLES);
      if (local && local.length > 0) {
        memoryArticlesCache = { data: local, timestamp: Date.now() };
        return local;
      }
    }

    let articles: NewsArticle[] = [];

    if (db) {
      try {
        const colRef = collection(db, 'news_articles');
        const q = query(colRef, where('status', '==', 'published'));
        const snap = await getDocs(q);

        if (!snap.empty) {
          articles = snap.docs.map((d) => ({
            id: d.id,
            ...(d.data() as any),
          })) as NewsArticle[];
        }
      } catch (err) {
        console.warn('[NEWS SERVICE] Firestore query notice:', err);
      }
    }

    // STRICT HUMAN APPROVAL: Only articles explicitly approved by human admin can be published/visible
    articles = articles.filter((a) => a.status === 'published' && a.human_approved === true);

    // Sort by published_at desc
    articles.sort((a, b) => new Date(b.published_at || 0).getTime() - new Date(a.published_at || 0).getTime());

    memoryArticlesCache = { data: articles, timestamp: Date.now() };
    setCachedItem(STORAGE_KEY_ARTICLES, articles);

    return articles;
  },

  /**
   * Fetch all articles including drafts for Admin screen
   */
  async getAllArticlesForAdmin(): Promise<NewsArticle[]> {
    if (!db) {
      return [];
    }

    try {
      const colRef = collection(db, 'news_articles');
      const snap = await getDocs(colRef);

      if (!snap.empty) {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as any),
        })) as NewsArticle[];
        list.sort((a, b) => new Date(b.published_at || 0).getTime() - new Date(a.published_at || 0).getTime());
        return list;
      }
    } catch (err) {
      console.warn('[NEWS SERVICE] Admin fetch notice:', err);
    }

    return [];
  },

  /**
   * Save or update article (Admin action)
   */
  async saveArticle(article: Partial<NewsArticle>): Promise<string> {
    const id = article.id || `article-${Date.now()}`;
    const now = new Date().toISOString();

    let titleEn = (article.title_en || '').trim();
    let titleFr = (article.title_fr || '').trim();
    let excerptEn = (article.excerpt_en || '').trim();
    let excerptFr = (article.excerpt_fr || '').trim();
    let contentEn = (article.content_en || '').trim();
    let contentFr = (article.content_fr || '').trim();

    const isEnEmpty = !titleEn || titleEn === 'Untitled Article';
    const isFrEmpty = !titleFr || titleFr === 'Article sans titre';

    if (isFrEmpty && !isEnEmpty) {
      try {
        const trans = await translatePostOrArticleAsync({
          title: titleEn,
          excerpt: excerptEn,
          content: contentEn,
          sourceLang: 'en',
        });
        titleFr = trans.title_fr;
        if (!excerptFr) excerptFr = trans.excerpt_fr;
        if (!contentFr) contentFr = trans.content_fr;
      } catch (err) {
        console.warn('[newsService] Auto-translate to FR note:', err);
      }
    } else if (isEnEmpty && !isFrEmpty) {
      try {
        const trans = await translatePostOrArticleAsync({
          title: titleFr,
          excerpt: excerptFr,
          content: contentFr,
          sourceLang: 'fr',
        });
        titleEn = trans.title_en;
        if (!excerptEn) excerptEn = trans.excerpt_en;
        if (!contentEn) contentEn = trans.content_en;
      } catch (err) {
        console.warn('[newsService] Auto-translate to EN note:', err);
      }
    }

    if (!titleEn) titleEn = titleFr || 'Untitled Article';
    if (!titleFr) titleFr = titleEn || 'Article sans titre';

    const CATEGORY_MAP: Record<string, string> = {
      'Legal & Titre Foncier': 'Droit Foncier & Cadastre',
      'Diaspora Guide': 'Guide Diaspora',
      'Market Trends': 'Tendances du Marché',
      'Infrastructure & Growth': 'Infrastructures & Croissance',
      'Agricultural & Commercial': 'Agropastoral & Commercial',
    };

    const category = article.category || 'Market Trends';
    const categoryFr = article.category_fr || CATEGORY_MAP[category] || category;

    const fullArticle: NewsArticle = {
      id,
      slug: article.slug || id,
      title_en: titleEn,
      title_fr: titleFr,
      excerpt_en: excerptEn,
      excerpt_fr: excerptFr,
      content_en: contentEn,
      content_fr: contentFr,
      category,
      category_fr: categoryFr,
      image_url: article.image_url || 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=1200&q=80',
      reading_time_minutes: Number(article.reading_time_minutes) || 4,
      published_at: article.published_at || now,
      status: article.status || 'published',
      human_approved: article.human_approved !== undefined ? article.human_approved : true,
      approved_by: article.approved_by,
      approved_at: article.approved_at,
      is_pinned: Boolean(article.is_pinned),
      author: article.author || 'Cameroon Land Market Editorial',
      views_count: article.views_count || 0,
    };

    if (db) {
      const docRef = doc(db, 'news_articles', id);
      await setDoc(docRef, fullArticle, { merge: true });
    }

    // Invalidate caches
    memoryArticlesCache = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY_ARTICLES);
    }

    return id;
  },

  /**
   * Fetch a single article by unique slug or ID across:
   * 1. news_articles collection
   * 2. articles collection
   * 3. publisher_articles collection (for newly authored/generated articles)
   * 4. local storage & default articles
   */
  async getArticleBySlug(slugOrId: string): Promise<NewsArticle | null> {
    if (!slugOrId) return null;
    const cleanSlug = slugOrId.trim().toLowerCase();

    // 1. Check published cache and memory
    const list = await this.getPublishedArticles();
    const found = list.find(
      (a) =>
        a.slug?.toLowerCase() === cleanSlug ||
        a.id?.toLowerCase() === cleanSlug
    );
    if (found) return found;

    // 2. Query Firestore news_articles
    if (db) {
      try {
        const directSnap = await getDoc(doc(db, 'news_articles', slugOrId));
        if (directSnap.exists()) {
          return { id: directSnap.id, ...(directSnap.data() as any) } as NewsArticle;
        }

        const q = query(collection(db, 'news_articles'), where('slug', '==', slugOrId));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const d = snap.docs[0];
          return { id: d.id, ...(d.data() as any) } as NewsArticle;
        }
      } catch (err) {
        console.warn('[NEWS SERVICE] Query news_articles notice:', err);
      }

      // 3. Query Firestore articles collection
      try {
        const directSnap = await getDoc(doc(db, 'articles', slugOrId));
        if (directSnap.exists()) {
          return { id: directSnap.id, ...(directSnap.data() as any) } as NewsArticle;
        }

        const q = query(collection(db, 'articles'), where('slug', '==', slugOrId));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const d = snap.docs[0];
          return { id: d.id, ...(d.data() as any) } as NewsArticle;
        }
      } catch (err) {
        console.warn('[NEWS SERVICE] Query articles notice:', err);
      }

      // 4. Query Firestore publisher_articles (allows immediate preview & SEO resolution)
      try {
        const directSnap = await getDoc(doc(db, 'publisher_articles', slugOrId));
        if (directSnap.exists()) {
          return this.formatPublisherArticle(directSnap.id, directSnap.data());
        }

        const q1 = query(collection(db, 'publisher_articles'), where('slug_en', '==', slugOrId));
        const snap1 = await getDocs(q1);
        if (!snap1.empty) {
          return this.formatPublisherArticle(snap1.docs[0].id, snap1.docs[0].data());
        }

        const q2 = query(collection(db, 'publisher_articles'), where('slug_fr', '==', slugOrId));
        const snap2 = await getDocs(q2);
        if (!snap2.empty) {
          return this.formatPublisherArticle(snap2.docs[0].id, snap2.docs[0].data());
        }
      } catch (err) {
        console.warn('[NEWS SERVICE] Query publisher_articles notice:', err);
      }
    }

    // 5. Check LocalStorage publisher articles
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem('clm_publisher_articles_v1');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            const p = parsed.find(
              (item) =>
                item.id === slugOrId ||
                item.slug_en === slugOrId ||
                item.slug_fr === slugOrId ||
                item.slug_en?.toLowerCase() === cleanSlug ||
                item.slug_fr?.toLowerCase() === cleanSlug
            );
            if (p) {
              return this.formatPublisherArticle(p.id, p);
            }
          }
        }
      } catch {}
    }

    return null;
  },

  /**
   * Converts a PublisherArticle (with WordPress Gutenberg blocks) to a NewsArticle
   */
  formatPublisherArticle(id: string, p: any): NewsArticle {
    const renderBlocks = (blocks: any[]): string => {
      if (!blocks || !Array.isArray(blocks)) return '';
      return blocks
        .map((b) => {
          switch (b.type) {
            case 'heading': {
              const lvl = b.level || 2;
              return `<h${lvl} class="font-serif font-bold text-slate-900 mt-8 mb-4 text-${lvl === 2 ? '2xl' : 'xl'}">${b.content || ''}</h${lvl}>`;
            }
            case 'paragraph':
              return `<p class="text-slate-800 leading-relaxed my-4 text-base">${b.content || ''}</p>`;
            case 'pullquote':
              return `<blockquote class="my-8 p-6 bg-emerald-50/70 border-l-4 border-emerald-600 rounded-r-2xl italic text-slate-800 font-medium text-lg shadow-xs"><p class="mb-2">“${b.content || ''}”</p>${b.authorQuote ? `<cite class="text-xs uppercase tracking-wider font-bold text-emerald-800 not-italic block mt-2">— ${b.authorQuote}</cite>` : ''}</blockquote>`;
            case 'callout': {
              const variantStyles = {
                info: 'bg-blue-50 border-blue-400 text-blue-950',
                warning: 'bg-amber-50 border-amber-500 text-amber-950',
                statute: 'bg-emerald-50 border-emerald-600 text-emerald-950',
                success: 'bg-teal-50 border-teal-500 text-teal-950',
              }[b.calloutVariant || 'info'];
              return `<div class="my-6 p-5 border-l-4 rounded-xl ${variantStyles}"><h5 class="font-bold text-sm uppercase tracking-wide mb-1">⚖️ Statutory Provision / Regulatory Notice</h5><div class="text-sm leading-relaxed">${b.content || ''}</div></div>`;
            }
            case 'image':
              return `<figure class="my-8 ${b.alignment === 'wide' ? 'w-full' : 'max-w-3xl mx-auto'}"><img src="${b.url}" alt="${b.alt || ''}" class="w-full h-80 sm:h-96 object-cover rounded-2xl shadow-md" loading="lazy" decoding="async" />${b.caption ? `<figcaption class="text-center text-xs text-slate-500 mt-2 italic">${b.caption}</figcaption>` : ''}</figure>`;
            case 'table': {
              const headers = (b.tableHeaders || []).map((h: string) => `<th class="p-3 bg-slate-100 font-bold text-left text-xs text-slate-700">${h}</th>`).join('');
              const rows = (b.tableRows || [])
                .map((r: string[]) => `<tr class="border-t border-slate-200">${r.map((c: string) => `<td class="p-3 text-xs text-slate-800">${c}</td>`).join('')}</tr>`)
                .join('');
              return `<div class="overflow-x-auto my-6 border border-slate-200 rounded-xl shadow-xs"><table class="w-full text-left border-collapse"><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table></div>`;
            }
            case 'checklist': {
              const items = (b.items || []).map((it: string) => `<li class="flex items-start gap-2.5 my-2 text-sm text-slate-800"><span class="text-emerald-600 font-bold">✓</span><span>${it}</span></li>`).join('');
              return `<ul class="my-6 space-y-2 p-5 bg-slate-50 border border-slate-200/80 rounded-2xl">${items}</ul>`;
            }
            case 'faq': {
              const faqs = (b.faqItems || [])
                .map((f: any) => `<div class="border border-slate-200 rounded-xl p-4 my-3 bg-white"><h4 class="font-bold text-sm text-slate-900 mb-1">Q: ${f.question}</h4><p class="text-xs text-slate-700 leading-relaxed">A: ${f.answer}</p></div>`)
                .join('');
              return `<div class="my-8"><h3 class="font-serif font-bold text-xl text-slate-900 mb-4">Frequently Asked Statutory Questions</h3>${faqs}</div>`;
            }
            default:
              return `<p class="my-4 text-slate-800">${b.content || ''}</p>`;
          }
        })
        .join('\n\n');
    };

    const contentEn = renderBlocks(p.blocks_en);
    const contentFr = renderBlocks(p.blocks_fr && p.blocks_fr.length > 0 ? p.blocks_fr : p.blocks_en);

    return {
      id,
      slug: p.slug_en || id,
      title_en: p.title_en || 'Untitled Guide',
      title_fr: p.title_fr || p.title_en || 'Guide Sans Titre',
      excerpt_en: p.excerpt_en || '',
      excerpt_fr: p.excerpt_fr || p.excerpt_en || '',
      content_en: contentEn,
      content_fr: contentFr,
      category: p.category || 'Market Trends',
      category_fr: p.category_fr || p.category || 'Tendances du Marché',
      image_url:
        p.featured_image?.url ||
        'https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=1200&q=80',
      reading_time_minutes: p.reading_time_minutes || 5,
      published_at: p.published_at || p.created_at || new Date().toISOString(),
      status: p.status || 'published',
      is_pinned: false,
      author: p.author || 'Cameroon Land Market Editorial Desk',
      views_count: 0,
    };
  },

  /**
   * Delete article (Admin action)
   */
  async deleteArticle(id: string): Promise<boolean> {
    if (db) {
      try {
        const docRef = doc(db, 'news_articles', id);
        await deleteDoc(docRef);
      } catch (err) {
        console.error('[NEWS SERVICE] Delete error:', err);
        return false;
      }
    }

    // Invalidate caches
    memoryArticlesCache = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY_ARTICLES);
    }

    return true;
  },

  /**
   * Invalidate all local caches
   */
  invalidateCache(): void {
    memoryArticlesCache = null;
    memoryLayoutCache = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY_ARTICLES);
      localStorage.removeItem(STORAGE_KEY_LAYOUT);
    }
  },
};
